import { colors } from '@/theme/foundations/colors';
import { KText } from '@/ui-library/components/text';
import { Input, Flex, Icon, InputGroup, Tooltip } from '@chakra-ui/react';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { useField } from 'formik';
import { useRef, useState } from 'react';
import { currencyFormatterHelpers } from '@/utils/currencyFormatter';
import {
  handleNumberInputChange,
  handleNumberKeyDown as validateNumberKeyDown,
  handleNumberPaste as validateNumberPaste,
} from './numberFieldValidation';
const { formatCurrencyNumber, cleanCurrencyInput } = currencyFormatterHelpers;

interface InputFieldProps {
  item?: Record<string, unknown>;
  field: any;
  formik?: any;
}

function MaxLengthCounter({
  currentLength,
  maxLength,
  disabled,
  showError,
}: {
  currentLength: number;
  maxLength: number;
  disabled?: boolean;
  showError?: boolean;
}) {
  return (
    <KText
      size="sm-medium"
      pr="5px"
      background={'transparent'}
      borderTopRightRadius="6px"
      borderBottomRightRadius="6px"
      color={
        disabled ? 'secondary-300' : showError ? 'error-200' : 'secondary-700'
      }
    >
      {currentLength}/{maxLength}
    </KText>
  );
}

export const InputField = ({ item = {}, field, formik }: InputFieldProps) => {
  const {
    name,
    inputType,
    isEditable = true,
    placeholder = '',
    label,
    style,
    maxLength,
    disabled,
    showCopy,
    CustomLeftElement,
    CustomRightElement,
    inputRef,
    showErrorStyles = false,
    isRequired,
    formatType,
    currencyFieldName,
    minValue = 0,
    maxValue,
    stepValue,
    isInputSubmit = false,
    customValidation,
    AfterInputGroupContent,
    customOnChange = () => {},
    customOnKeyDown = () => {},
    beforeMaxLength,
    afterMaxLength = true,
    debounceOnType = () => {},
    autoFocus = false,
    onPaste,
  } = item as any;

  const [tooltipMessage, setTooltipMessage] = useState('');
  const [_field, meta, helpers] = useField(name);
  const { setTouched, setValue } = helpers;
  const { submitCount } = formik || {};
  const { value, touched } = meta || {};
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [, currencyMeta] = useField(
    currencyFieldName || 'dummy_currency_field'
  );
  const selectedCurrencyId = currencyFieldName ? currencyMeta?.value : null;

  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);

  if (!item) return null;

  const showError =
    (submitCount > 0 || touched) &&
    (!(value?.toString()?.length > 0) || meta?.error) &&
    !showErrorStyles &&
    isRequired;

  const defaultStyles = {
    fontSize: '14px',
    paddingInlineStart: '.75em',
    p: '14px 10px',
    height: '44px',
    color: colors['secondary-700'],
    backgroundColor: !isEditable && '#F7F7F7',
  };

  const handleCopy = () => {
    const inputValue = field.value || '';
    navigator.clipboard.writeText(inputValue);
  };

  const formatNumberWithCommas = (value) => {
    if (!value) return '';

    const numericValue = (value?.toString() || '')
      .split('.')[0]
      .replace(/[^\d]/g, '');

    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getSelectedCurrencyString = () => {
    const currencies = (
      item as { currencies?: Array<{ id: unknown; currency?: string }> }
    ).currencies;
    if (!currencyFieldName || !selectedCurrencyId || !currencies) return null;
    const selectedCurrency = currencies.find(
      (c) => c.id === selectedCurrencyId
    );
    return selectedCurrency?.currency || null;
  };

  const formattedValue = (() => {
    if (formatType === 'currency') {
      const currencyString = getSelectedCurrencyString();
      if (currencyString && currencyFieldName) {
        return formatCurrencyNumber(field?.value || '', currencyString);
      }
      return formatNumberWithCommas(field?.value || '');
    }
    return field?.value || '';
  })();

  const handleOnChange = (event) => {
    clearTimeout(intervalRef.current);
    const value = event.target.value;
    customOnChange(event);

    intervalRef.current = setTimeout(() => {
      debounceOnType(value);
    }, 1000);

    if (customValidation) {
      customValidation(value, event, field.onChange);
    } else if (inputType === 'number') {
      handleNumberInputChange(value, setValue, triggerPasteTooltip);
    } else if (formatType !== 'currency') {
      field.onChange(event);
    } else {
      const cleanedValue = cleanCurrencyInput(value);
      setValue(cleanedValue);
    }
  };

  const triggerPasteTooltip = (message) => {
    setTooltipMessage(message);
    clearTimeout(tooltipTimeoutRef.current);
    tooltipTimeoutRef.current = setTimeout(() => {
      setTooltipMessage('');
    }, 2000);
  };

  const handleNumberKeyDown = (e) => {
    validateNumberKeyDown(e, {
      stepValue,
      minValue,
      maxValue,
      setValue,
    });
  };

  const onPasteHandler = (event) => {
    if (inputType === 'number') validateNumberPaste(event, triggerPasteTooltip);
    onPaste?.(event, setValue);
  };

  const isDisabled = !!(disabled || !isEditable);

  return (
    <Tooltip
      isOpen={!!tooltipMessage}
      label={tooltipMessage}
      placement="left"
      hasArrow
      shouldWrapChildren
      backgroundColor={'pastel-6-light'}
      color={'pastel-6-dark'}
    >
      <InputGroup
        w="100%"
        minH="44px"
        alignItems="center"
        transition="background-color 0.15s ease, border-color 0.15s ease"
        bg={
          showError
            ? 'pastel-1-light'
            : isDisabled
              ? 'secondary-25'
              : 'base-white'
        }
        _hover={{
          borderColor: showError ? 'error-200' : 'secondary-300',
          bg: showError
            ? 'pastel-1-light'
            : isDisabled
              ? 'secondary-25'
              : 'secondary-25',
          boxShadow: 'none',
        }}
        _focusWithin={{
          borderColor: showError ? 'error-200' : 'secondary-300',
          bg: showError ? 'pastel-1-light' : 'secondary-25',
          boxShadow: 'none',
        }}
        border="1px solid"
        borderColor={showError ? 'error-200' : 'secondary-100'}
        outlineOffset="0px !important"
        borderRadius="6px"
        {...style}
      >
        {CustomLeftElement && <CustomLeftElement showError={showError} />}
        <Input
          {...field}
          onChange={handleOnChange}
          onClick={() => {
            if (inputRef?.current) {
              inputRef.current?.focus();
            } else if (localInputRef?.current) {
              localInputRef.current?.focus();
            }
          }}
          background="transparent"
          color={showError ? colors['error-200'] : colors['secondary-700']}
          ref={inputRef || localInputRef}
          id={name}
          variant="outline"
          autoComplete="off"
          _placeholder={{
            opacity: 1,
            fontSize: '14px',
            fontWeight: 'medium',
            color: showError ? 'error-100' : 'secondary-500',
          }}
          _hover={{
            outline: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',
          }}
          _focus={{
            outline: 'none !important',
            border: 'none !important',
            boxShadow: 'none !important',
          }}
          _invalid={{
            border: 'none !important',
            boxShadow: 'none !important',
            background: 'transparent',
          }}
          type={inputType !== 'number' ? inputType || 'text' : undefined}
          inputMode={inputType === 'number' ? 'decimal' : undefined}
          disabled={disabled || !isEditable}
          value={formattedValue}
          placeholder={
            placeholder || `Enter ${label ? label?.toLowerCase() : ''}`
          }
          maxLength={maxLength}
          min={minValue}
          max={maxValue}
          step={stepValue}
          onFocus={() => setTouched(false)}
          onBlur={() => setTouched(true)}
          onWheel={(e) => {
            if (inputType === 'number') {
              e.currentTarget.blur();
            }
          }}
          onKeyDown={(e) => {
            if (customOnKeyDown) customOnKeyDown(e);
            if (e.key === 'Enter') {
              e.preventDefault();
              if (formik && isInputSubmit) formik.submitForm();
            }
            if (inputType === 'number') handleNumberKeyDown(e);
          }}
          autoFocus={autoFocus}
          onPaste={onPasteHandler}
          {...defaultStyles}
          {...style}
          border="none !important"
          outline="none !important"
          boxShadow="none !important"
        />
        {CustomRightElement && beforeMaxLength && (
          <CustomRightElement showError={showError} />
        )}
        {!!maxLength && (
          <MaxLengthCounter
            currentLength={String(field?.value ?? '').length}
            maxLength={maxLength}
            disabled={disabled}
            showError={showError}
          />
        )}
        {CustomRightElement && afterMaxLength && !beforeMaxLength && (
          <CustomRightElement showError={showError} />
        )}
        {showCopy && (
          <Flex
            gap=".5em"
            m="0 2em"
            alignItems="center"
            onClick={handleCopy}
            opacity={!field?.value ? 0.5 : 1}
            cursor={!field?.value ? 'not-allowed' : 'pointer'}
          >
            <Icon as={Copy03} color="ats-primary" />
            <KText color="primary-500">Copy</KText>
          </Flex>
        )}
      </InputGroup>
      {AfterInputGroupContent && <AfterInputGroupContent />}
    </Tooltip>
  );
};
